/**
 * useCurrentUser — typed adapter around `useSession()` that surfaces the
 * subset of `/auth/me` payload the dashboard surface consumes.
 *
 * Returns `null` when the session is loading, unauthenticated, error, OR
 * when the payload doesn't shape-match the contract (defensive against
 * silent backend drift). Returns `{ id, email }` only when fully
 * validated.
 *
 * Wire shape source of truth:
 *   `apps/api/src/modules/identity/interface/dto/auth-me.response.dto.ts`.
 * The shared validator at
 *   `packages/validators/src/identity/auth/auth-me-response.schema.ts`
 * is currently a subset of the DTO (no `platform`, no per-membership
 * `organizationSlug` / `role` / `permissions`); we narrow defensively
 * here rather than depend on the schema being complete. Adding the
 * missing fields to the schema is out of scope for SCRUM-58 — flagged
 * as a future hygiene ticket.
 *
 * The `name` field is intentionally absent from the public surface
 * because `/auth/me` does not currently ship one. The dashboard's
 * greeting hero falls back to "there" per the dashboard surface
 * (Sketched in Claude Design 2026-05-10).
 */

import { useSession } from './useSession.js';

export interface CurrentUser {
  readonly id: string;
  readonly email: string;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

export function useCurrentUser(): CurrentUser | null {
  const session = useSession();
  if (session.status !== 'authenticated') return null;
  const user = session.user;
  if (user === null || typeof user !== 'object') return null;
  const candidate = user as Record<string, unknown>;
  if (!isNonEmptyString(candidate.id)) return null;
  if (!isNonEmptyString(candidate.email)) return null;
  return { id: candidate.id, email: candidate.email };
}
